// src/components/auth/Login.js

import React, { useState, useContext } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  TextField,
  Button,
  Typography,
  Container,
  Box,
  Paper,
  Alert,
  Avatar,
  ThemeProvider,
  createTheme,
} from '@mui/material';
import logoImage from '../../assets/schoonerlogo.png';
import { AuthContext } from '../../context/AuthContext';

const theme = createTheme({
  palette: {
    primary: { main: '#3f51b5' },
    secondary: { main: '#4caf50' },
    background: { default: '#f5f5f5' },
  },
  typography: {
    fontFamily: '"Poppins","Roboto","Helvetica","Arial",sans-serif',
    h4: { fontWeight: 600 },
  },
  components: {
    MuiButton: { styleOverrides: { root: { borderRadius: 8, textTransform: 'none', fontWeight: 600, padding: '10px 0' } } },
    MuiTextField: { styleOverrides: { root: { marginBottom: 16 } } },
    MuiPaper: { styleOverrides: { root: { borderRadius: 12, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' } } },
  },
});

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const { login } = useContext(AuthContext);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const response = await login(email, password);
      if (response.user.role === 'manager' || response.user.role === 'reports_viewer') navigate('/dashboard');
      else navigate('/time-clock');
    } catch (err) {
      setError(
        err.response?.data?.message ||
          'Network error. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Container maxWidth="sm" sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center' }}>
        <Paper elevation={0} sx={{ p: 5, width: '100%', backgroundColor: 'white', border: '1px solid rgba(0,0,0,0.05)' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 4 }}>
            <Avatar sx={{ m: 1, bgcolor: 'white', width: 56, height: 56 }}>
              <img
                src={`${logoImage}?v=${new Date().getTime()}`}
                alt="Schooner Punch Clock"
                style={{ width: '100%', height: '100%' }}
              />
            </Avatar>
            <Typography variant="h4" component="h1" sx={{ mt: 2, textAlign: 'center' }}>
              Schooner <br /> PunchClock
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
              Sign in to access your account
            </Typography>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>}

          <Box component="form" onSubmit={handleSubmit} noValidate>
            <TextField
              required
              fullWidth
              label="Email Address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <TextField
              required
              fullWidth
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              sx={{ mt: 2, mb: 3 }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              sx={{ py: 1.5 }}
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <Link to="/forgot-password" style={{ color: theme.palette.primary.main }}>Forgot password?</Link>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Don't have an account?{' '}
                <Link to="/register" style={{ color: theme.palette.primary.main }}>Sign up here</Link>
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Container>
    </ThemeProvider>
  );
};

export default Login;
